import { Operation as LogOperation, Log, LogCategory } from '@augloop/logging';
import { LogCountLimiter, Utils } from '../utils';
import { createConnectionReliabilityManager } from './iconnection-reliability-manager';
// Rate control thresholds for logging purpose
const rpsLoggingThreshold = 50;
const bpsLoggingThreshold = 100000;
export class NetworkRateController {
    constructor(networkRateSettings, closeWorkerOnReliabilityFailureEnabled) {
        var _a, _b, _c, _d, _e;
        this.logCountLimiter = new LogCountLimiter(NetworkRateController.className);
        this.emptyMessageId = 0;
        this.egressMessageCount = 0;
        this.egressByteCount = 0;
        this.prevSeq = -1;
        this.pendingQueue = [];
        this.egressRateLogOp = new LogOperation({ operationName: 'NetworkEgressRate', success: true });
        this.egressLogOp = new LogOperation({ operationName: 'NetworkEgressControl', success: true }).start();
        this.egressedCache = new Map();
        // Default or use host-provided settings
        this.rpsThreshold = (_a = networkRateSettings === null || networkRateSettings === void 0 ? void 0 : networkRateSettings.rpsThreshold) !== null && _a !== void 0 ? _a : 150;
        this.bpsThreshold = (_b = networkRateSettings === null || networkRateSettings === void 0 ? void 0 : networkRateSettings.bpsThreshold) !== null && _b !== void 0 ? _b : 500000000; //500MB;
        this.pendingMaxSize = (_c = networkRateSettings === null || networkRateSettings === void 0 ? void 0 : networkRateSettings.pendingMaxSize) !== null && _c !== void 0 ? _c : 10000;
        this.egressedMaxSize = (_d = networkRateSettings === null || networkRateSettings === void 0 ? void 0 : networkRateSettings.egressedMaxSize) !== null && _d !== void 0 ? _d : 10000;
        this.messageMaxSize = (_e = networkRateSettings === null || networkRateSettings === void 0 ? void 0 : networkRateSettings.messageMaxSize) !== null && _e !== void 0 ? _e : 3000000; //3MB imposed by server
        this.closeWorkerOnReliabilityFailureEnabled = closeWorkerOnReliabilityFailureEnabled !== null && closeWorkerOnReliabilityFailureEnabled !== void 0 ? closeWorkerOnReliabilityFailureEnabled : true;
        this.initPromise = new Promise((resolve) => { this.resolveInitPromise = resolve; });
    }
    init(worker, ingress, queryEgressCacheSize, networkMode, clientMetadata) {
        this.worker = worker;
        this.ingress = ingress;
        this.queryEgressCacheSize = queryEgressCacheSize;
        this.clientMetadata = clientMetadata;
        this.egressRateControlTimer = setInterval(() => this.flush(), 1000);
        this.networkMode = networkMode;
        this.reliabilityManager = createConnectionReliabilityManager(this.worker, this.close.bind(this), this.closeWorkerOnReliabilityFailureEnabled);
        this.resolveInitPromise();
    }
    open() {
        this.initPromise.then(() => {
            if (!this.reliabilityManager) {
                this.collectTelemetry(false /*success*/, 'OnOpenFailure', 'Reliability Manager is undefined');
                return;
            }
            this.collectTelemetry(true /*success*/, 'OnOpen');
            this.egressRateControlIntervalStart = Date.now();
            this.reliabilityManager.start();
            this.flush();
        }).catch((error) => {
            this.collectTelemetry(false /*success*/, 'OnOpenFailure', error);
        });
    }
    ingressFromWorker(message, onIngressParsed) {
        if (!this.reliabilityManager) {
            this.collectTelemetry(false /*success*/, 'ingressFromWorkerFailure', 'Reliability Manager is undefined');
            return;
        }
        if (!this.reliabilityManager.needsParsedResponses() && this.reliabilityManager.isReliabilityResponse(message)) {
            // If we don't need parsed responses, we can call onResponse before parsing
            // Ping pong message is just a string and doesn't need to be parsed
            this.reliabilityManager.onResponse();
        }
        else {
            this.ingress(message, (response) => {
                onIngressParsed === null || onIngressParsed === void 0 ? void 0 : onIngressParsed(response);
                if (this.reliabilityManager.needsParsedResponses() && this.reliabilityManager.isReliabilityResponse(response)) {
                    // If we are using http, we need to parse the message to see if it is a reliability response
                    this.reliabilityManager.onResponse(response);
                }
            });
        }
    }
    close() {
        var _a;
        this.collectTelemetry(true /*success*/, 'OnClose');
        (_a = this.reliabilityManager) === null || _a === void 0 ? void 0 : _a.close();
        this.worker = undefined;
        this.prevSeq = -1;
        this.clearEgressControlTimeout();
        this.reliabilityManager = undefined;
    }
    clearMessageQueues() {
        this.pendingQueue = [];
        this.egressedCache.clear();
    }
    /**
     * Receives message to be sent from upstream. Send it if possible, else put it in pending queue
     *
     * @param message message to be sent, containing seqId, messageId, and isSessionInitMessage flag
     * @returns None
     */
    egress(message) {
        var _a;
        if (!this.reliabilityManager) {
            this.collectTelemetry(false /*success*/, 'EgressFailure', 'Reliability Manager is undefined');
            return;
        }
        const messageSize = this.getMessageSize(message);
        if (messageSize >= this.messageMaxSize) {
            this.collectTelemetry(true /*success*/, `Message size exceeded ${this.messageMaxSize}`, `${messageSize}`);
            return;
        }
        if ((message.obj instanceof ArrayBuffer || message.isHttpSessionInitMessage) && this.worker) {
            this.worker.egress(message);
            return;
        }
        this.validateSyncMessage(message.messageId, message.seqId);
        const isEgressRateExceeded = this.egressMessageCount >= this.rpsThreshold || this.egressByteCount >= this.bpsThreshold;
        const egressCacheSize = this.queryEgressCacheSize();
        const isEgressSizeLimitExceeded = this.pendingQueue.length > this.pendingMaxSize || egressCacheSize > this.egressedMaxSize;
        const isConnected = this.reliabilityManager.checkConnection();
        const isPendingQueueEmpty = this.pendingQueue.length == 0;
        if (!isConnected || isEgressRateExceeded || !isPendingQueueEmpty || isEgressSizeLimitExceeded) {
            // Do not send through network worker yet if egressRate isn't healthy, we aren't connected, or we have pending messages
            let signature = '';
            signature += this.pendingQueue.length >= this.pendingMaxSize ? 'Pending queue max exceeded. ' : (!isPendingQueueEmpty ? 'Pending queue not empty' : '');
            signature += egressCacheSize >= this.egressedMaxSize ? 'Egressed cache max exceeded. ' : '';
            signature += this.egressMessageCount >= this.rpsThreshold ? 'RPS exceeded. ' : '';
            signature += this.egressByteCount >= this.bpsThreshold ? 'BPS exceeded. ' : '';
            signature += !isConnected ? 'Not connected' : '';
            const description = `pendingQueueSize: ${this.pendingQueue.length} | egressCacheSize: ${egressCacheSize} | rpsThreshold: ${this.rpsThreshold} | bpsThreshold: ${this.bpsThreshold}`;
            this.collectTelemetry(true /*success*/, signature, description);
            // Do not send through network worker yet if any of the conditions is true
            const messageId = (_a = message.messageId) !== null && _a !== void 0 ? _a : `id${this.emptyMessageId++}`;
            this.pendingQueue.push({ obj: message.obj, seqId: message === null || message === void 0 ? void 0 : message.seqId, messageId: messageId, isHttpSessionInitMessage: message === null || message === void 0 ? void 0 : message.isHttpSessionInitMessage });
            return;
        }
        this.sendToNetworkWorker(message);
    }
    getMessageSize(message) {
        return message.obj instanceof ArrayBuffer ? message.obj.byteLength : message.obj.length;
    }
    /**
     * Send the message to the network worker. Also cache it and wait to be acked.
     *
     * @param message message to be sent, also containing the messageId
     */
    sendToNetworkWorker(message) {
        try {
            if (!this.worker) {
                this.collectTelemetry(false /*success*/, 'SendToNetworkWorkerFailure', 'NetworkWorker is undefined');
                return;
            }
            if (!this.reliabilityManager) {
                this.collectTelemetry(false /*success*/, 'SendToNetworkWorkerFailure', 'Reliability Manager is undefined');
                return;
            }
            this.worker.egress(message);
            this.reliabilityManager.postEgress();
            const messageSize = this.getMessageSize(message);
            // Keep track of egress rate
            this.logEgressCount(messageSize);
        }
        catch (e) {
            this.collectTelemetry(false /*success*/, 'sendToWorker', e ? e.message : '');
        }
    }
    /**
     * Flush out pending messages
     */
    flush() {
        if (!this.reliabilityManager) {
            this.collectTelemetry(false /*success*/, 'FlushFailure', 'Reliability Manager is undefined');
            return;
        }
        let flushedCount = 0;
        let flushedBytes = 0;
        while (this.pendingQueue.length > 0
            && this.queryEgressCacheSize() < this.egressedMaxSize
            && flushedCount < this.rpsThreshold
            && flushedBytes < this.bpsThreshold
            && this.reliabilityManager.checkConnection()) {
            const message = this.pendingQueue.shift();
            this.sendToNetworkWorker(message);
            flushedCount++;
            flushedBytes += this.getMessageSize(message);
        }
    }
    clearEgressControlTimeout() {
        if (this.egressRateControlTimer) {
            clearInterval(this.egressRateControlTimer);
            this.egressRateControlTimer = undefined;
        }
    }
    /**
     * Check for sequence gap in Sync messages.
     * The seq is expected to be continuous.
     *
     * @param messageId message id of the sync message
     * @param seq sequence id of the Sync message
     */
    validateSyncMessage(messageId, seqId) {
        // Skip validation if message or seq id is absent or it is a resent message
        if (seqId === undefined || messageId === undefined || seqId <= this.prevSeq) {
            return;
        }
        if (seqId && seqId !== this.prevSeq + 1) {
            this.collectTelemetry(true /*success*/, 'abandonedSyncMessage', 'Gap in sync message', [`${this.prevSeq}`, `${seqId}`, `${seqId - this.prevSeq}`]);
        }
        this.prevSeq = seqId;
    }
    logEgressCount(byteCount) {
        var _a;
        const now = Date.now();
        if (now - this.egressRateControlIntervalStart > 1000) {
            if (this.egressMessageCount > rpsLoggingThreshold || this.egressByteCount > bpsLoggingThreshold) {
                this.egressRateLogOp.start();
                this.egressRateLogOp.resultDescription = this.egressMessageCount > rpsLoggingThreshold ? 'rps limit exceeded' : '';
                this.egressRateLogOp.resultDescription = this.egressByteCount > bpsLoggingThreshold ? 'bps limit exceeded' : '';
                this.egressRateLogOp.dimension0 = (now - this.egressRateControlIntervalStart).toString();
                this.egressRateLogOp.dimension1 = `${this.egressMessageCount}`;
                this.egressRateLogOp.dimension2 = `${this.egressByteCount}`;
                this.egressRateLogOp.dimension3 = `networkMode: ${this.networkMode}`;
                Log.info(0x1e545710 /* tag_4vf2q */, LogCategory.CoreDefault, this.egressRateLogOp.stop());
            }
            if (((_a = this.clientMetadata) === null || _a === void 0 ? void 0 : _a.releaseAudienceGroup) !== 'Production') {
                // Help figuring out whether the default limits are appropriate
                const egressCacheSize = this.queryEgressCacheSize();
                this.collectTelemetry(true /*success*/, `networkMode: ${this.networkMode}`, 'egress', 
                // Collect magnitude of these variables for better log aggregation
                [
                    `${this.pendingQueue.length === 0 ? '0' : this.pendingQueue.length.toString().length}`,
                    `${egressCacheSize === 0 ? '0' : egressCacheSize.toString().length}`,
                    `${this.rpsThreshold} | ${this.bpsThreshold}`,
                ]);
            }
            this.egressRateControlIntervalStart = now;
            this.egressMessageCount = 0;
            this.egressByteCount = 0;
        }
        this.egressMessageCount++;
        this.egressByteCount += (byteCount !== null && byteCount !== void 0 ? byteCount : 0);
    }
    collectTelemetry(success, signature, description, dimensions) {
        const dim = new Array(4).fill('');
        if (dimensions) {
            for (let i = 0; i < dimensions.length; i++) {
                dim[i] = dimensions[i];
            }
        }
        // Add network mode to the dimensions if there is space
        dim[3] = dim[3] ? dim[3] : `networkMode: ${this.networkMode}`;
        Utils.collectTelemetry(this.egressLogOp, this.logCountLimiter, success, description, signature, dim);
    }
}
NetworkRateController.className = 'NetworkRateController';
//# sourceMappingURL=network-rate-controller.js.map